import React, {useEffect, useState} from 'react';
import { Container, Paper, Title, Grid, TextInput, Checkbox, MultiSelect, Text } from '@mantine/core';
import { Formik, Form } from 'formik';
import {connect, useSelector} from 'react-redux';
import {bindActionCreators, Dispatch} from 'redux';
import {CommonState} from '../../../redux';
import { orderStore } from '../../../redux/entities/inventory/order';
import { itemStore } from '../../../redux/entities/inventory/item';
import { categoryStore } from '../../../redux/entities/inventory/category';
import { AppTheme } from '../../../appTheme';
import {useMount} from "../../../hooks/useMount";
import {AxiosError} from "axios";
import {handleAxiosError} from "../../../util/http";
import {CenteredSpinner} from "../../../components/util/widgets/CenteredSpinner/CenteredSpinner";
import {CenteredErrorMessage} from "../../../components/util/widgets/CenteredErrorMessage/CenteredErrorMessage";
import {loadProductSelectionStateData} from "../../../redux/stateResponses/productSelectionState";
import OrderItemInput from '../../../components/util/form-components/OrderItemInput/OrderItemInput';
import {loadInventoryManagementData} from "../../../redux/stateResponses/inventoryManagement";
import { getExport } from '../../../api/exportApi';

export type ExportForm = {
  startDate: string; // ISO yyyy-mm-dd
  endDate: string;   // ISO yyyy-mm-dd
  organizations: string[]; // organization names
  includeAllItems: boolean;
  items: string[]; // item ids
};

type OwnProps = { onLoadingChange?: (loading: boolean) => void };
type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & OwnProps;

const Export = (props: Props) => {
  const orders = useSelector((s: CommonState) => orderStore.selectors.getAsArray(s));
  const items = useSelector((s: CommonState) => itemStore.selectors.unarchivedItems(s));
  const categories = useSelector((s: CommonState) => categoryStore.selectors.getAsArray(s));

  const { actions: {loadInventoryData}, onLoadingChange } = props;
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useMount(async () => {
    try {
      await loadInventoryData();
    } catch (e: AxiosError | any) {
      setErrorMessage(handleAxiosError(e, {connectionMsg: 'Failed to load inventory'}));
    }
    setLoading(false);
  });

  // Notify parent when loading state changes (for modal-level spinner)
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const organizationOptions = React.useMemo(() => {
    // Derive organization names directly from orders (Order.organization is now a string)
    const names = (orders ?? [])
      .map(o => (o?.organization ?? '').trim())
      .filter(n => !!n);
    const unique = Array.from(new Set(names));
    return unique
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ value: name, label: name }));
  }, [orders]);

  // Selection is handled inside OrderItemInput via Formik FieldArray

  const initialValues: ExportForm = {
    startDate: '',
    endDate: '',
    organizations: [],
    includeAllItems: true,
    items: [],
  };

  const renderContent = () => (
    <Formik<ExportForm>
      initialValues={initialValues}
      onSubmit={async (vals, { setSubmitting }) => {
        setErrorMessage(undefined);
        try {
          const { blob, filename } = await getExport(vals);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename || 'export-report';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        } catch (e: any) {
          setErrorMessage(handleAxiosError(e, { connectionMsg: 'Failed to start export' }));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit, isSubmitting }) => {

        const handleIncludeAllToggle = (checked: boolean) => {
          setFieldValue('includeAllItems', checked);
          if (checked) {
            // Clear manual selections when including all
            setFieldValue('items', []);
          }
        };

        return (
        <Form onSubmit={handleSubmit} id="export-form">
          <Grid gutter="md">
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="Start date"
                placeholder="YYYY-MM-DD"
                value={values.startDate}
                onChange={(e) => setFieldValue('startDate', e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                type="date"
                label="End date"
                placeholder="YYYY-MM-DD"
                value={values.endDate}
                onChange={(e) => setFieldValue('endDate', e.currentTarget.value)}
              />
            </Grid.Col>
          </Grid>
        </Form>
      );
      }}
    </Formik>
  );

  return loading ?  <CenteredSpinner/> : (errorMessage ? <CenteredErrorMessage message={errorMessage} /> : renderContent());
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    loadInventoryData: loadInventoryManagementData
  }, dispatch)});
const mapStateToProps = (_state: CommonState) => ({
});
export default connect(mapStateToProps, mapDispatchToProps)(Export);
